import '../../styles/live-theme.css';

import React, { useEffect, useRef, useState } from 'react';
import { Radio, Sparkles, Rewind, Film, Wifi, Heart, Cast } from 'lucide-react';
import { LIVE, goldGrad, goldGradText } from '../../lib/liveTheme';

// ─── GlowOrbs ─────────────────────────────────────────────────────
/**
 * Ambient fixed radial-gradient gold orbs for background depth.
 * pointer-events-none, positioned behind all content (z-0).
 */
export function GlowOrbs(): React.ReactElement {
  return (
    <div aria-hidden="true" style={{ pointerEvents: 'none' }}>
      {/* Top-left orb */}
      <div
        style={{
          position: 'fixed',
          top: '-10%',
          left: '-8%',
          width: '520px',
          height: '520px',
          borderRadius: '50%',
          background: `radial-gradient(circle at center, ${LIVE.goldDim} 0%, transparent 70%)`,
          zIndex: 0,
          opacity: 0.7,
        }}
      />
      {/* Bottom-right orb */}
      <div
        style={{
          position: 'fixed',
          bottom: '-12%',
          right: '-6%',
          width: '480px',
          height: '480px',
          borderRadius: '50%',
          background: `radial-gradient(circle at center, rgba(201,169,98,0.10) 0%, transparent 70%)`,
          zIndex: 0,
          opacity: 0.6,
        }}
      />
      {/* Centre accent */}
      <div
        style={{
          position: 'fixed',
          top: '38%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '200px',
          borderRadius: '50%',
          background: `radial-gradient(ellipse at center, rgba(201,169,98,0.05) 0%, transparent 75%)`,
          zIndex: 0,
          opacity: 0.5,
        }}
      />
    </div>
  );
}

// ─── GoldShimmerText ───────────────────────────────────────────────
interface GoldShimmerTextProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Playfair heading with looping gold shimmer animation.
 * Uses .live-shimmer CSS class; animation paused for reduced-motion users.
 */
export function GoldShimmerText({
  children,
  className = '',
}: GoldShimmerTextProps): React.ReactElement {
  return (
    <span className={`font-playfair live-shimmer ${className}`}>
      {children}
    </span>
  );
}

// ─── GoldButton ───────────────────────────────────────────────────
interface GoldButtonProps {
  children: React.ReactNode;
  variant?: 'solid' | 'ghost';
  href?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * Primary CTA button.
 * solid — gold fill with black text, glow on hover.
 * ghost — transparent with gold border, gold text.
 * Renders <a> when href is provided, <button> otherwise.
 */
export function GoldButton({
  children,
  variant = 'solid',
  href,
  onClick,
  className = '',
  type = 'button',
  disabled = false,
  'aria-label': ariaLabel,
}: GoldButtonProps): React.ReactElement {
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 32px',
    borderRadius: '6px',
    fontFamily: 'Inter, system-ui, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    textDecoration: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease',
    border: 'none',
    outline: 'none',
    position: 'relative' as const,
    whiteSpace: 'nowrap' as const,
  };

  const solidStyle: React.CSSProperties = {
    ...baseStyle,
    background: 'linear-gradient(135deg, var(--lw-accent, #c9a962) 0%, var(--lw-accent-2, #e8c14e) 100%)',
    color: '#0a0a0a',
    boxShadow: '0 0 0 rgba(201,169,98,0)',
  };

  const ghostStyle: React.CSSProperties = {
    ...baseStyle,
    background: 'transparent',
    color: 'var(--lw-accent, #c9a962)',
    border: `1px solid var(--lw-border, rgba(201,169,98,0.5))`,
  };

  const style = variant === 'solid' ? solidStyle : ghostStyle;

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>): void => {
    const el = e.currentTarget as HTMLElement;
    if (variant === 'solid') {
      el.style.boxShadow = `0 4px 24px rgba(201,169,98,0.40)`;
      el.style.filter = 'brightness(1.08)';
    } else {
      el.style.background = 'var(--lw-card, rgba(201,169,98,0.14))';
      el.style.borderColor = 'var(--lw-accent, #c9a962)';
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>): void => {
    const el = e.currentTarget as HTMLElement;
    if (variant === 'solid') {
      el.style.boxShadow = '0 0 0 rgba(201,169,98,0)';
      el.style.filter = '';
    } else {
      el.style.background = 'transparent';
      el.style.borderColor = 'var(--lw-border, rgba(201,169,98,0.5))';
    }
  };

  if (href) {
    return (
      <a
        href={href}
        style={style}
        className={className}
        aria-label={ariaLabel}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement>}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      type={type}
      style={style}
      className={className}
      disabled={disabled}
      aria-label={ariaLabel}
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </button>
  );
}

// ─── GlassCard ────────────────────────────────────────────────────
interface GlassCardProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Frosted-glass card with a top gold hairline accent.
 * Uses .live-glass for backdrop-filter + border; adds a 2px
 * gold top strip for extra visual anchor.
 */
export function GlassCard({
  children,
  className = '',
  style: extraStyle,
}: GlassCardProps): React.ReactElement {
  return (
    <div
      className={`live-glass ${className}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        ...extraStyle,
      }}
    >
      {/* Gold top accent hairline */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(135deg, var(--lw-accent, #c9a962) 0%, var(--lw-accent-2, #e8c14e) 100%)',
          borderRadius: '16px 16px 0 0',
        }}
      />
      {children}
    </div>
  );
}

// ─── LiveBadge ────────────────────────────────────────────────────
type LiveStatus = 'live' | 'replay' | 'offline';

interface LiveBadgeProps {
  status: LiveStatus;
}

const BADGE_CONFIG: Record<
  LiveStatus,
  { label: string; bg: string; dot: string; pulse: boolean }
> = {
  live: {
    label: 'LIVE',
    bg: 'rgba(232,47,47,0.15)',
    dot: LIVE.live,
    pulse: true,
  },
  replay: {
    label: 'REPLAY',
    bg: 'rgba(255,255,255,0.08)',
    dot: 'rgba(255,255,255,0.40)',
    pulse: false,
  },
  offline: {
    label: 'OFFLINE',
    bg: 'rgba(255,255,255,0.05)',
    dot: 'rgba(255,255,255,0.25)',
    pulse: false,
  },
};

/**
 * Status pill: red pulsing dot for live, static grey for replay/offline.
 */
export function LiveBadge({ status }: LiveBadgeProps): React.ReactElement {
  const cfg = BADGE_CONFIG[status];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '999px',
        background: cfg.bg,
        border: `1px solid ${status === 'live' ? 'rgba(232,47,47,0.35)' : 'rgba(255,255,255,0.10)'}`,
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.10em',
        color: status === 'live' ? LIVE.live : 'var(--lw-text-muted, rgba(255,255,255,0.40))',
      }}
      aria-label={`Stream status: ${status}`}
    >
      <span
        className={cfg.pulse ? 'live-pulse' : ''}
        style={{
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: cfg.dot,
          flexShrink: 0,
          display: 'inline-block',
        }}
        aria-hidden="true"
      />
      {cfg.label}
    </span>
  );
}

// ─── SectionLabel ─────────────────────────────────────────────────
interface SectionLabelProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Uppercase gold eyebrow label above section headings.
 */
export function SectionLabel({
  children,
  className = '',
}: SectionLabelProps): React.ReactElement {
  return (
    <p
      className={`font-inter ${className}`}
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: '0.70rem',
        fontWeight: 600,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: 'var(--lw-accent, #c9a962)',
        margin: 0,
      }}
    >
      {children}
    </p>
  );
}

// ─── GoldRule ─────────────────────────────────────────────────────
interface GoldRuleProps {
  className?: string;
}

/**
 * Centre-fading gold horizontal hairline divider.
 */
export function GoldRule({ className = '' }: GoldRuleProps): React.ReactElement {
  return (
    <hr
      className={`live-hairline ${className}`}
      style={{ margin: '0', border: 'none' }}
      aria-hidden="true"
    />
  );
}

// ─── CountdownClock ───────────────────────────────────────────────
interface CountdownClockProps {
  target: string | Date;
  className?: string;
}

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function computeTimeLeft(target: string | Date): TimeLeft | null {
  const diff = new Date(target).getTime() - Date.now();
  if (diff <= 0) return null;
  const totalSeconds = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

/**
 * Live countdown to a target date/string.
 * Shows days · hrs · min · sec in gold Playfair numerals.
 * Returns null (nothing rendered) when the target is past.
 */
export function CountdownClock({
  target,
  className = '',
}: CountdownClockProps): React.ReactElement | null {
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(() =>
    computeTimeLeft(target)
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setTimeLeft(computeTimeLeft(target));

    intervalRef.current = setInterval(() => {
      const next = computeTimeLeft(target);
      setTimeLeft(next);
      if (!next && intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    }, 1000);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [target]);

  if (!timeLeft) return null;

  const units: Array<{ label: string; value: number }> = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hrs', value: timeLeft.hours },
    { label: 'Min', value: timeLeft.minutes },
    { label: 'Sec', value: timeLeft.seconds },
  ];

  return (
    <div
      className={className}
      role="timer"
      aria-label="Countdown to event"
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '20px',
      }}
    >
      {units.map(({ label, value }, idx) => (
        <React.Fragment key={label}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            <span
              className="font-playfair"
              style={{
                ...goldGradText,
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                fontWeight: 700,
                lineHeight: 1,
                minWidth: '2.4ch',
                textAlign: 'center',
              }}
            >
              {String(value).padStart(2, '0')}
            </span>
            <span
              className="font-inter"
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '0.60rem',
                fontWeight: 500,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--lw-text-muted, rgba(255,255,255,0.40))',
              }}
            >
              {label}
            </span>
          </div>
          {/* Separator dot between units (not after last) */}
          {idx < units.length - 1 && (
            <span
              aria-hidden="true"
              style={{
                color: 'var(--lw-accent, #c9a962)',
                fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                lineHeight: 1,
                marginBottom: '0.5rem',
                opacity: 0.7,
              }}
            >
              ·
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── ExpectMarquee ("What to expect" ribbon) ──────────────────────
/**
 * ExpectMarquee — an elegant auto-scrolling ribbon of icon+label promise chips
 * that sells the LA Mirage Live experience (4K, DVR, keepsake, watch-anywhere)
 * without a dense feature grid. Two duplicated chip sets scroll seamlessly;
 * reduced-motion falls back to a static, horizontally-scrollable row.
 *
 * Shared by LiveWatch (pre-live) and LiveLanding (What-we-deliver continuity).
 */
export const EXPECT_CHIPS: { Icon: typeof Sparkles; label: string }[] = [
  { Icon: Sparkles, label: "True 4K Ultra HD" },
  { Icon: Rewind,   label: "Live Rewind" },
  { Icon: Film,     label: "Kept as a Keepsake" },
  { Icon: Wifi,     label: "Flawless on Any Connection" },
  { Icon: Heart,    label: "Cheer From Afar" },
  { Icon: Cast,     label: "Watch Anywhere" },
];

export function ExpectMarquee(): React.ReactElement {
  const chipSet = (hidden: boolean) =>
    EXPECT_CHIPS.map(({ Icon, label }, i) => (
      <div key={`${hidden ? "b" : "a"}-${i}`} className="lw-expect-chip" aria-hidden={hidden || undefined}>
        <Icon size={15} strokeWidth={1.75} aria-hidden="true" />
        <span>{label}</span>
      </div>
    ));
  return (
    <div className="lw-expect" aria-label="What to expect from LA Mirage Live">
      <div className="lw-expect-track">
        {chipSet(false)}
        {chipSet(true)}
      </div>
    </div>
  );
}

// ─── LiveIcon (convenience re-export of lucide Radio) ────────────
/** Lucide <Radio /> icon pre-sized for live indicator use. */
export { Radio as LiveIcon };
